import { useEffect, useMemo, useRef, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import WalkinSummary from "@/components/WalkinSummary";
import WalkinQrPanel from "@/components/WalkinQrPanel";
import QuestionnaireSubmissions from "@/components/QuestionnaireSubmissions";
import { nameProblem, autoFixName } from "@/lib/names";
import ConsultRecordings from "@/components/ConsultRecordings";
import SalesMode from "@/components/SalesMode";
import {
  listLeads, getLead, createLead, updateLead, deleteLead, leadsStats,
  getRecordingsByLead, listScripts, uploadRecording, listCustomers, getUnattachedRecordings, attachRecording, resyncLeadToGhl,
  getWalkinWaiting, startWalkin, walkinNoConsult, linkWalkin, getLeadWalkin, getWalkinForm,
  listLeadPayments, addLeadPayment, deleteLeadPayment, feMonthly, jointPaymentCandidates,
  getProgrammeCatalog, getObjections, logLeadFollowup, listFollowupDue,
} from "@/lib/api";
import { taxIncluded, numberLocale } from "@/lib/money";
import BillButton from "@/components/BillButton";
import TakePayment from "@/components/TakePayment";
import ConfirmDetails from "@/components/ConfirmDetails";
import BillHistory from "@/components/BillHistory";
import TaxSplit from "@/components/TaxSplit";
import { usePaymentModes } from "@/lib/paymentModes";
import { guaranteeProblem, cleanGuarantee } from "@/lib/guarantee";
import { FE_PROGRAMMES, autoEndDate } from "@/lib/programmes";
import { PERIOD_OPTIONS, rowInPeriod, todayLocal, monthRangeOf, monthLabelOf } from "@/lib/periods";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { RosterSelect } from "@/components/RosterAutocomplete";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { atLeast } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Search, Mic2, IndianRupee, TrendingUp, Users, CheckCircle2, XCircle, Pencil, BellRing, Clock, History, ChevronDown } from "lucide-react";
import { AuditHistoryDialog } from "@/components/AuditHistoryDialog";
import EmailTypoHint from "@/components/EmailTypoHint";

const ANNUAL_BLOOD_TEST_ADDON = 3500;   // ex-GST, India (the only country with blood tests)

const STATUS_OPTIONS = [
  { value: "not_sold", label: "Not Sold" },
  { value: "sold", label: "Sold" },
  { value: "sold_pending_payment", label: "Sold — Pending Payment" },
  { value: "closed_lost", label: "Closed — Lost" },
];
const FEPIF = ["FE", "PIF", "PT"];
// Self-bought 1 Week Trials (backend routes/trial_week.py) are sold consults: a filter and a label of their own.
const TRIAL_PROGRAMME = "1 Week Trial";
const isTrialPaid = (l) => (l.programme || "").trim().toLowerCase() === TRIAL_PROGRAMME.toLowerCase()
  && (l.lead_status || "").startsWith("sold");
const pillStatus = (l) => (isTrialPaid(l) ? "trial_week_paid" : l.lead_status);

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => {
  const v = (n || 0) * 100;
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
};

export default function Sales() {
  const askDelete = useDeleteAsk();
  const { user, activeFacility } = useAuth();
  const consultPay = !!activeFacility?.consult_pay;   // this gym takes the first payment in FPA
  const [leads, setLeads] = useState([]);
  const [recCount, setRecCount] = useState({});   // lead_id → number of call recordings
  const [onlyMissingRec, setOnlyMissingRec] = useState(false);
  const [salesMode, setSalesMode] = useState(false);
  const [pendingRec, setPendingRec] = useState(null);       // a saved recording waiting for its consult
  const [declinedRec, setDeclinedRec] = useState(false);    // the member said no in Sales mode
  const [qrFirst, setQrFirst] = useState(false);            // Sales mode without a questionnaire: point to the QR first
  const [qrRequest, setQrRequest] = useState(0);
  const [unattached, setUnattached] = useState([]);
  const [waiting, setWaiting] = useState([]);              // pre-sale questionnaires awaiting a consult
  const [walkin, setWalkin] = useState(null);              // the pre-sale questionnaire this consult is for
  // A group consult: one rep, several people at once, one recording. {id, queue: forms still to fill in, total, n}
  const [group, setGroup] = useState(null);
  const [params, setParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [salesExFilter, setSalesExFilter] = useState("all");   // one rep's consults (CR-0030)
  const [period, setPeriod] = useState(() => (new URLSearchParams(window.location.search).get("q") ? "all" : "this_month"));  // Week/Month/…, or "custom"; all time when opened from Accounts with ?q=
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [addOpen, setAddOpen] = useState(false);
  // Bumped every time the New Lead dialog opens so LeadDialog remounts with a fresh state —
  // guarantees the form starts blank instead of showing the previous entry's values.
  const [addKey, setAddKey] = useState(0);
  const [edit, setEdit] = useState(null);
  const [paying, setPaying] = useState(null);       // consult whose first payment is being taken in FPA

  const refresh = async () => {
    const [l, s, r] = await Promise.all([listLeads(), leadsStats(), getRecordingsByLead().catch(() => ({}))]);
    setLeads(l);
    setStats(s);
    setRecCount(r || {});
    getUnattachedRecordings().then((d) => setUnattached(d.recordings || [])).catch(() => setUnattached([]));
    getWalkinWaiting().then((d) => setWaiting(d.forms || [])).catch(() => setWaiting([]));
  };
  useEffect(() => { refresh(); }, []);
  // New questionnaires show up without a reload: check every 30 seconds while the page is open.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") getWalkinWaiting().then((d) => setWaiting(d.forms || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);
  // /sales?walkin=<id> (from the menu badge or a shared link) opens that form's summary.
  useEffect(() => {
    const id = params.get("walkin");
    if (!id) return;
    getWalkinForm(id).then((f) => setWaiting((w) => (w.some((x) => x.id === f.id) ? w : [f, ...w]))).catch(() => {});
    params.delete("walkin");
    setParams(params, { replace: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // /sales?lead=<id> (from the pending-payments reminder) opens that consult for editing, also
  // when this page is already open (CR-0026).
  const leadParam = params.get("lead");
  useEffect(() => {
    if (!leadParam) return;
    getLead(leadParam).then(setEdit).catch(() => toast.error("Couldn't open that consult. It may be at another gym."));
    params.delete("lead");
    setParams(params, { replace: true });
  }, [leadParam]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Start a consult from a pre-sale questionnaire: Sales mode first; the consult form opens pre-filled after.
  const beginWalkin = async (form, { record = true } = {}) => {
    try {
      const f = await startWalkin(form.id);
      setWalkin(f);
      const said = f.recording_consent ?? form.recording_consent;
      // Said no on the questionnaire: never record; the consult is saved as "Member declined".
      if (said === false) { setPendingRec(null); setDeclinedRec(true); setAddKey((k) => k + 1); setAddOpen(true); }
      else if (record) setSalesMode(said === true ? "agreed" : true);   // agreed: recording starts by itself
      else { setPendingRec(null); setAddKey((k) => k + 1); setAddOpen(true); }
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start this consult"); }
  };
  // Several people from the waiting list consulted together: one Sales-mode recording, then a consult form each.
  const beginGroup = async (forms) => {
    try {
      const started = [];
      for (const form of forms) started.push(await startWalkin(form.id));
      const consent = started.map((f, i) => f.recording_consent ?? forms[i].recording_consent);
      setGroup({ id: newGroupId(), queue: started.slice(1), total: started.length, n: 1 });
      setWalkin(started[0]);
      // Anyone said no to recording: nothing is recorded for the group.
      if (consent.some((c) => c === false)) { setPendingRec(null); setDeclinedRec(true); setAddKey((k) => k + 1); setAddOpen(true); }
      else setSalesMode(consent.every((c) => c === true) ? "agreed" : true);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start this group consult"); }
  };
  const closeWalkin = async (form) => {
    try { await walkinNoConsult(form.id); toast.success(`${form.name} taken off the waiting list`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };

  // Consults in the chosen period/status/search, before the "no recording" filter — the
  // recordings report counts over these.
  const inScope = useMemo(() => {
    return leads.filter((l) => {
      if (!rowInPeriod(l.date, period, customRange)) return false;
      if (statusFilter === "trial_week_paid") { if (!isTrialPaid(l)) return false; }
      else if (statusFilter !== "all" && l.lead_status !== statusFilter) return false;
      if (salesExFilter !== "all" && (l.sales_ex || "") !== salesExFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        return [l.name, l.phone, l.email, l.sales_ex, l.lead_ex].some(
          (v) => (v || "").toString().toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [leads, statusFilter, salesExFilter, query, period, customRange]);
  const groupSize = useMemo(() => {
    const n = {};
    leads.forEach((l) => { if (l.group_id) n[l.group_id] = (n[l.group_id] || 0) + 1; });
    return n;
  }, [leads]);
  const salesExOptions = useMemo(
    () => [...new Set(leads.map((l) => (l.sales_ex || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [leads]);
  const filtered = useMemo(
    () => (onlyMissingRec ? inScope.filter((l) => !recCount[l.id] && l.recording_skipped_reason !== "declined") : inScope),
    [inScope, onlyMissingRec, recCount],
  );

  // A back-dated entry would vanish under "This month": switch the list to its month.
  const revealDate = (date) => {
    if (!date || rowInPeriod(date, period, customRange)) return;
    setPeriod("custom");
    setCustomRange(monthRangeOf(date));
    toast(`Showing ${monthLabelOf(date)} — this entry is dated ${String(date).slice(8, 10)}-${String(date).slice(5, 7)}-${String(date).slice(0, 4)}`);
  };


  const remove = async (id) => {
    const reason = askDelete("this consult", "Delete this lead?");
    if (reason === null) return;
    try { if (deletionRequested(await deleteLead(id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Lead deleted");
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="sales-page">
      {/* Phones: Sales mode stays pinned at the top while scrolling */}
      <div className="sm:hidden sticky z-10 -mx-4 -mt-5 px-4 pt-3 pb-2 bg-[#0a0a0a]/95 backdrop-blur" style={{ top: "var(--app-header-h, 61px)" }}>
        <Button type="button" onClick={() => setQrFirst(true)} data-testid="sales-mode-btn-mobile"
          className="w-full h-12 text-base bg-rose-600 hover:bg-rose-500 active:bg-rose-700 rounded-full shadow-lg shadow-rose-900/40">
          <Mic2 className="h-5 w-5 mr-2" /> Sales mode · record a consult
        </Button>
      </div>
      {/* Monthly summary strip */}
      <FEMonthlyStrip />

      {/* Follow-ups due (Not-Sold nudges) */}
      <FollowupsDueStrip onRefresh={refresh} />

      {salesMode && (
        <SalesMode
          preAgreed={salesMode === "agreed"}
          questionnaire={walkin}
          onClose={() => {
            setSalesMode(false);
            // From a questionnaire with no recording (e.g. the member declined): open the consult form anyway.
            if (walkin) { setPendingRec(null); setAddKey((k) => k + 1); setAddOpen(true); }
          }}
          onSaved={(recording) => { setSalesMode(false); setPendingRec(recording); setAddKey((k) => k + 1); setAddOpen(true); refresh(); }}
          onDeclined={() => { setSalesMode(false); setPendingRec(null); setDeclinedRec(true); setAddKey((k) => k + 1); setAddOpen(true); }}
        />
      )}

      {/* The process: every consult starts with the pre-sale questionnaire (it asks about recording too) */}
      <Dialog open={qrFirst} onOpenChange={setQrFirst}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md sm:rounded-2xl" data-testid="questionnaire-first">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl">Start with the pre-sale questionnaire</DialogTitle>
            <DialogDescription className="text-zinc-400 text-base">
              Every consult starts with the questionnaire. Let the member scan the QR. They&apos;ll show up under
              &quot;Waiting for a consult&quot;, then tap Proceed to Sales Consult.
            </DialogDescription>
          </DialogHeader>
          <Button type="button" onClick={() => { setQrFirst(false); setQrRequest((n) => n + 1); }} data-testid="questionnaire-first-qr"
            className="h-12 rounded-full bg-rose-600 hover:bg-rose-500 text-base">Show the QR</Button>
          <p className="text-xs text-zinc-500">No questionnaire, no consult. That&apos;s the LIFTR process.</p>
        </DialogContent>
      </Dialog>
      <PaymentsMovedNotice />
      <WalkinQrPanel showRequest={qrRequest} />
      <WaitingList forms={waiting} onStart={beginWalkin} onStartGroup={beginGroup} onClose={closeWalkin} />

      {/* Recordings saved in Sales mode whose consult wasn't saved yet */}
      {unattached.length > 0 && (
        <section className="card-surface p-4 border-amber-500/30" data-testid="unattached-recordings">
          <div className="eyebrow text-amber-300">Recordings not linked to a consult yet</div>
          <div className="mt-2 space-y-2">
            {unattached.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Mic2 className="h-4 w-4 text-amber-300" />
                <span className="text-zinc-200">{new Date(r.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
                {r.recorded_seconds ? <span className="text-zinc-500">· {r.recorded_seconds < 60 ? "under 1 min" : `${Math.round(r.recorded_seconds / 60)} min`}</span> : null}
                <Button type="button" size="sm" className="ml-auto rounded-full bg-blue-600 hover:bg-blue-500"
                  onClick={() => { setPendingRec({ id: r.id, name: r.filename, seconds: r.recorded_seconds }); setAddKey((k) => k + 1); setAddOpen(true); }}>
                  Add consult details
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Call recordings report for the consults in the current period/filters */}
      <RecordingCoverage leads={inScope} recCount={recCount} onlyMissing={onlyMissingRec} setOnlyMissing={setOnlyMissingRec} />

      {/* Everyone who filled in the pre-sale questionnaire, and whether they had a consult (CR-0072) */}
      <QuestionnaireSubmissions />

      {/* Leads table */}
      <section className="pt-2">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="eyebrow">New Consult · new member consults</div>
            <h2 className="font-display text-2xl font-bold mt-1">All new consults</h2>
            <p className="text-sm text-zinc-400 mt-1">The consult and its outcome. A sold consult's payments are taken on its sale in All Sales.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select data-testid="sales-select-111" value={period} onValueChange={setPeriod}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-[calc(50%-0.25rem)] sm:w-40" data-testid="leads-period-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                {(PERIOD_OPTIONS || []).map((p) => (
                  <SelectItem data-testid="sales-select-item-117" key={p.value} value={p.value} className="focus:bg-[#1a1a1a]">{p.label}</SelectItem>
                ))}
                <SelectItem value="custom" className="focus:bg-[#1a1a1a]" data-testid="leads-period-custom">Custom dates…</SelectItem>
              </SelectContent>
            </Select>
            {period === "custom" && (
              <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="leads-custom-range">
                <div className="flex-1 sm:flex-none sm:w-36"><DatePicker value={customRange.from} onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))} placeholder="From" data-testid="leads-range-from" /></div>
                <span className="text-xs text-zinc-500">to</span>
                <div className="flex-1 sm:flex-none sm:w-36"><DatePicker value={customRange.to} onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))} placeholder="To" data-testid="leads-range-to" /></div>
              </div>
            )}
            <div className="relative w-full sm:w-auto max-sm:-order-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="leads-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, phone…"
                className="pl-9 bg-[#0a0a0a] border-[#262626] w-full sm:w-56"
              />
            </div>
            <Select data-testid="sales-select-131" value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-[calc(50%-0.25rem)] sm:w-44" data-testid="leads-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                <SelectItem data-testid="sales-select-item-136" value="all" className="focus:bg-[#1a1a1a]">All statuses</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem data-testid="sales-select-item-138" key={o.value} value={o.value} className="focus:bg-[#1a1a1a]">{o.label}</SelectItem>
                ))}
                <SelectItem data-testid="sales-select-item-trial" value="trial_week_paid" className="focus:bg-[#1a1a1a]">Trial Week Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={salesExFilter} onValueChange={setSalesExFilter}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-[calc(50%-0.25rem)] sm:w-44" data-testid="leads-salesex-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                <SelectItem value="all" className="focus:bg-[#1a1a1a]">All Sales Ex</SelectItem>
                {salesExOptions.map((n) => <SelectItem key={n} value={n} className="focus:bg-[#1a1a1a]">{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" onClick={() => setQrFirst(true)} data-testid="sales-mode-btn"
              className="max-sm:hidden bg-rose-600 hover:bg-rose-500 rounded-full px-5">
              <Mic2 className="h-4 w-4 mr-1.5" /> Sales mode
            </Button>
            <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (o) setAddKey((k) => k + 1); if (!o) { setPendingRec(null); setWalkin(null); setGroup(null); refresh(); } }}>
              <DialogTrigger data-testid="sales-dialog-trigger-143" asChild>
                <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5 max-sm:fixed max-sm:right-4 max-sm:z-30 max-sm:bottom-[calc(5rem+env(safe-area-inset-bottom))] max-sm:h-12 max-sm:shadow-lg max-sm:shadow-black/50" data-testid="new-lead-btn" data-write>
                  <Plus className="h-4 w-4 mr-1.5" /> New lead
                </Button>
              </DialogTrigger>
              <LeadDialog
                key={addKey}
                mode="create"
                current={user}
                leads={leads}
                recCount={recCount}
                pendingRec={pendingRec}
                memberDeclined={declinedRec}
                walkin={walkin}
                group={group}
                onDone={(saved, next) => {
                  if (next) {               // the next person in this group consult: same recording, a fresh form
                    const g = next.group;
                    const [nextForm, ...rest] = g.queue || [];
                    setGroup({ ...g, queue: rest, n: g.n + 1 });
                    setWalkin(nextForm || null);
                    setAddKey((k) => k + 1);
                    toast(`Group consult: now ${nextForm ? nextForm.name : "the next person"}`);
                    refresh();
                    return;
                  }
                  setAddOpen(false); setPendingRec(null); setDeclinedRec(false); setWalkin(null); setGroup(null); refresh(); revealDate(saved?.date);
                }}
              />
            </Dialog>
          </div>
        </div>

        {/* Phones: one card per consult */}
        <div className="md:hidden space-y-2 pb-16" data-testid="lead-cards">
          {filtered.map((l) => (
            <LeadCard key={l.id} l={l} groupN={l.group_id ? groupSize[l.group_id] : 0} recs={recCount[l.id] || 0} onEdit={() => setEdit(l)} onDelete={() => remove(l.id)} onBilled={refresh} />
          ))}
          {filtered.length === 0 && (
            <div className="card-surface px-4 py-10 text-center text-sm text-zinc-500">
              No consults {query || statusFilter !== "all" || salesExFilter !== "all" ? "match your filters" : "yet. Tap New lead to log the first one"}.
            </div>
          )}
        </div>

        <div className="card-surface overflow-hidden hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="px-4 py-3 font-medium">Sales Ex</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">FE/PIF</th>
                  <th className="px-4 py-3 font-medium text-right" title="Collected so far on sold consults, with any balance still due">Paid</th>
                  <th className="px-4 py-3 font-medium">Pay mode</th>
                  <th className="px-4 py-3 font-medium">Objection / Follow-up</th>
                  <th className="px-4 py-3 font-medium">Recording</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(filtered || []).map((l) => {
                  const nRecs = recCount[l.id] || 0;
                  const isNotSold = l.lead_status === "not_sold";
                  const hasFollowup = !!(l.followup_date || (l.comments || "").trim() || (l.objection || "").trim());
                  const needsFollowup = isNotSold && !hasFollowup;
                  const fmtFu = l.followup_date ? new Date(l.followup_date).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;
                  return (
                    <tr key={l.id} className={`border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors ${needsFollowup ? "bg-rose-500/[0.04]" : ""}`} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(l.date).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setEdit(l)} className="font-medium text-left hover:underline" data-testid={`lead-name-${l.id}`}>{l.name}</button>
                        {l.group_id && <GroupBadge n={groupSize[l.group_id]} />}
                        <div className="text-[11px] text-zinc-500">{l.phone || l.email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">
                        {l.sales_ex || "—"}
                        {l.lead_ex && <div className="text-[11px] text-zinc-500">Coach: {l.lead_ex}</div>}
                      </td>
                      <td className="px-4 py-3"><StatusPill value={pillStatus(l)} /></td>
                      <td className="px-4 py-3 text-zinc-300 text-xs font-mono">{l.fe_pif || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                        <PaidAmount l={l} />
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{l.mode_of_payment || "—"}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs max-w-[16rem]">
                        {needsFollowup ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-rose-500/40 bg-rose-500/10 text-rose-300" data-testid={`missing-followup-${l.id}`}>
                            No follow-up info
                          </span>
                        ) : (
                          <div className="space-y-0.5">
                            {l.objection && <div className="truncate" title={l.objection}>{l.objection}</div>}
                            {fmtFu && <div className="text-[10px] text-amber-400">Follow-up · {fmtFu}</div>}
                            {l.comments && <div className="text-[10px] text-zinc-500 truncate" title={l.comments}>“{l.comments}”</div>}
                            {!l.objection && !fmtFu && !l.comments && <span className="text-zinc-700">—</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setEdit(l)} data-testid={`lead-recordings-${l.id}`}
                          className={`inline-flex items-center gap-1.5 text-xs ${nRecs ? "text-blue-400 hover:text-blue-300" : "text-amber-300/90 hover:text-amber-200"}`}>
                          <Mic2 className="h-3 w-3" />{nRecs ? `${nRecs} recording${nRecs > 1 ? "s" : ""}` : "None · add"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {(l.lead_status === "sold" || l.lead_status === "sold_pending_payment") && (
                            <BillButton kind="lead" id={l.id} billed={l.billing_done} onBilled={refresh} />
                          )}
                          {consultPay && l.lead_status === "not_sold" && !Number(l.amount_paid) && (
                            <Button size="sm" onClick={() => setPaying(l)} className="h-7 bg-emerald-600 px-2 text-xs hover:bg-emerald-500"
                              data-testid={`take-payment-${l.id}`}>Take payment</Button>
                          )}
                          <button
                            onClick={() => setEdit(l)}
                            className="text-zinc-500 hover:text-blue-400 transition-colors p-1"
                            data-testid={`edit-lead-${l.id}`}
                            aria-label="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => remove(l.id)}
                            className="text-zinc-500 hover:text-rose-400 transition-colors p-1"
                            data-testid={`delete-lead-${l.id}`}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-14 text-center text-sm text-zinc-500">
                      No leads {query || statusFilter !== "all" || salesExFilter !== "all" ? "match your filters" : "yet — click New lead to log the first consult"}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) { setEdit(null); refresh(); } }}>
        {edit && (
          <LeadDialog
            mode="edit"
            initial={edit}
            current={user}
            leads={leads}
            recCount={recCount}
            onDone={(saved) => { setEdit(null); refresh(); revealDate(saved?.date); }}
          />
        )}
      </Dialog>
      <TakePayment lead={paying} open={!!paying} onClose={() => { setPaying(null); refresh(); }} onPaid={refresh} />
    </div>
  );
}

function WaitingList({ forms, onStart, onStartGroup, onClose }) {
  const [open, setOpen] = useState(true);
  const [show, setShow] = useState(null);           // the form whose summary is expanded
  const [picked, setPicked] = useState([]);         // ticked for a group consult
  if (!forms.length) return null;
  const pickable = forms.filter((f) => f.status !== "in_consult");
  const chosen = pickable.filter((f) => picked.includes(f.id));
  const ago = (iso) => {
    const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ago`;
  };
  return (
    <section className="card-surface p-4 border-rose-500/30" data-testid="walkin-waiting">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center gap-2 text-left">
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="eyebrow text-rose-300">Waiting for a consult</span>
        <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white">{forms.length}</span>
        <span className="ml-auto text-xs text-zinc-500 max-sm:hidden">Filled in the pre-sale questionnaire</span>
      </button>
      {open && (
        <ul className="mt-3 divide-y divide-[#1f1f1f]">
          {forms.map((f) => (
            <li key={f.id} className="py-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {f.status !== "in_consult" && pickable.length > 1 && (
                  <input type="checkbox" className="h-4 w-4 accent-violet-500" aria-label={`Consult ${f.name} together with others`}
                    checked={picked.includes(f.id)} data-testid={`walkin-pick-${f.id}`}
                    onChange={(e) => setPicked((p) => (e.target.checked ? [...p, f.id] : p.filter((x) => x !== f.id)))} />
                )}
                <button type="button" onClick={() => setShow(show === f.id ? null : f.id)} className="text-left min-w-0 flex-1 max-sm:basis-full">
                  <div className="text-zinc-100 font-medium">{f.name}</div>
                  <div className="text-xs text-zinc-500">
                    {ago(f.created_at)}{f.commitment ? ` · commitment ${f.commitment}/10` : ""}{(f.goals || []).length ? ` · ${f.goals.slice(0, 2).join(", ")}` : ""}
                    {f.status === "in_consult" && <span className="text-amber-300"> · with {f.claimed_by}</span>}
                    <span className={f.office?.hydrated ? "text-emerald-300" : "text-amber-300"}>
                      {" · "}{f.office?.hydrated === true ? "water given" : f.office?.hydrated === false ? "no water" : "water not marked"}
                    </span>
                    {f.measurements
                      ? <span className="text-zinc-400"> · {f.measurements.weight_kg} kg, waist {f.measurements.waist_cm} cm</span>
                      : <span className="text-amber-300" data-testid={`walkin-measure-pending-${f.id}`}> · measurements pending</span>}
                  </div>
                </button>
                <Button type="button" size="sm" variant="outline" className="rounded-full border-[#262626]" onClick={() => setShow(show === f.id ? null : f.id)}>
                  {show === f.id ? "Hide answers" : "See answers"}
                </Button>
                <Button type="button" size="sm" className="rounded-full bg-rose-600 hover:bg-rose-500" onClick={() => onStart(f)} data-testid="walkin-start">
                  <Mic2 className="h-4 w-4 mr-1" />{f.status === "in_consult" ? "Continue" : "Proceed to Sales Consult"}
                </Button>
                {f.recording_consent === false && <span className="text-xs text-zinc-400 basis-full">Said no to recording on the questionnaire</span>}
              </div>
              {show === f.id && (
                <div className="mt-3">
                  <WalkinSummary form={f} />
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <button type="button" className="text-zinc-400 underline" onClick={() => onStart(f, { record: false })}>Fill in the consult without recording</button>
                    <span className="text-zinc-700">·</span>
                    <button type="button" className="text-zinc-400 underline" onClick={() => onClose(f)}>Left without a consult</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && pickable.length > 1 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          {chosen.length >= 2 ? (
            <Button type="button" size="sm" className="rounded-full bg-violet-600 hover:bg-violet-500" data-testid="walkin-start-group"
              onClick={() => { onStartGroup(chosen); setPicked([]); }}>
              <Users className="h-4 w-4 mr-1" />Group consult with {chosen.map((f) => f.name.split(" ")[0]).join(", ")}
            </Button>
          ) : <span>Consulting more than one person at once? Tick them for a group consult.</span>}
        </div>
      )}
    </section>
  );
}

function LeadWalkin({ leadId }) {
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { getLeadWalkin(leadId).then((d) => setForm(d.form)).catch(() => {}); }, [leadId]);
  if (!form) return null;
  return (
    <div className="rounded-lg border border-[#262626] p-3" data-testid="lead-walkin">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 text-left text-sm">
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="text-zinc-200 font-medium">Pre-sale questionnaire answers</span>
        {form.commitment != null && <span className="text-xs text-zinc-500">· commitment {form.commitment}/10</span>}
      </button>
      {open && <div className="mt-3"><WalkinSummary form={form} /></div>}
    </div>
  );
}

function GhlSyncLine({ lead }) {
  const [res, setRes] = useState(lead.ghl_sync);
  const [busy, setBusy] = useState(false);
  const tone = res.state === "moved" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
    : res.state === "not_found" ? "text-amber-300 border-amber-500/30 bg-amber-500/5" : "text-rose-300 border-rose-500/30 bg-rose-500/5";
  const again = async () => {
    setBusy(true);
    try { setRes(await resyncLeadToGhl(lead.id)); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't sync"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${tone}`} data-testid="ghl-sync-line">
      <span className="font-medium">GoHighLevel:</span>
      <span>{res.message}</span>
      {res.owner_note && <span className={res.owner_note.startsWith("Owner set") ? "" : "text-amber-300"}>· {res.owner_note}</span>}
      {res.at && <span className="text-zinc-500">· {new Date(res.at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>}
      {res.state !== "moved" && (
        <button type="button" onClick={again} disabled={busy} className="ml-auto text-blue-400 hover:text-blue-300">{busy ? "Syncing…" : "Sync again"}</button>
      )}
    </div>
  );
}

function RecordingCoverage({ leads, recCount, onlyMissing, setOnlyMissing }) {
  const [open, setOpen] = useState(false);
  const total = leads.length;
  const withRec = leads.filter((l) => recCount[l.id]).length;
  const declined = leads.filter((l) => !recCount[l.id] && l.recording_skipped_reason === "declined").length;
  const missing = total - withRec - declined;          // a member who said no isn't a missed recording
  const pct = total ? Math.round((withRec / total) * 100) : 0;
  const byRep = useMemo(() => {
    const m = {};
    leads.forEach((l) => {
      const k = l.sales_ex || "Unassigned";
      m[k] = m[k] || { rep: k, total: 0, with: 0, declined: 0 };
      m[k].total += 1;
      if (recCount[l.id]) m[k].with += 1;
      else if (l.recording_skipped_reason === "declined") m[k].declined += 1;
    });
    return Object.values(m).sort((a, b) => (b.total - b.with - b.declined) - (a.total - a.with - a.declined) || b.total - a.total);
  }, [leads, recCount]);
  if (!total) return null;
  const tone = pct >= 90 ? "text-emerald-300" : pct >= 60 ? "text-amber-300" : "text-rose-300";
  return (
    <section className="card-surface p-4" data-testid="recording-coverage">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "" : "-rotate-90"}`} />
          <Mic2 className="h-4 w-4 text-zinc-400" />
          <span className="eyebrow">Call recordings</span>
        </button>
        <span className="text-sm">
          <b className={`tabular-nums ${tone}`}>{withRec} of {total}</b> <span className="text-zinc-400">consults have a recording ({pct}%)</span>
          {missing > 0 && <span className="text-zinc-400"> · <b className="text-rose-300 tabular-nums">{missing}</b> missing</span>}
          {declined > 0 && <span className="text-zinc-400"> · <b className="text-zinc-200 tabular-nums">{declined}</b> member declined</span>}
        </span>
        {missing > 0 && (
          <button type="button" onClick={() => setOnlyMissing(!onlyMissing)} data-testid="only-missing-recordings"
            className={`sm:ml-auto rounded-full border px-3 py-1 text-xs ${onlyMissing ? "border-rose-500/50 bg-rose-500/10 text-rose-200" : "border-[#262626] text-zinc-300 hover:border-rose-500/40"}`}>
            {onlyMissing ? "Showing only consults with no recording · show all" : "Show only consults with no recording"}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="py-2 pr-3 font-medium">Sales rep</th>
                <th className="py-2 px-3 font-medium text-right">Consults</th>
                <th className="py-2 px-3 font-medium text-right">Recorded</th>
                <th className="py-2 px-3 font-medium text-right">Declined</th>
                <th className="py-2 pl-3 font-medium text-right">Missing</th>
              </tr>
            </thead>
            <tbody>
              {byRep.map((r) => (
                <tr key={r.rep} className="border-b border-[#1a1a1a] last:border-0">
                  <td className="py-2 pr-3">{r.rep}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.total}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-zinc-400 whitespace-nowrap">{r.with} <span className="text-zinc-600 max-sm:hidden">({Math.round((r.with / r.total) * 100)}%)</span></td>
                  <td className="py-2 px-3 text-right tabular-nums text-zinc-400">{r.declined || "—"}</td>
                  <td className={`py-2 pl-3 text-right tabular-nums ${r.total - r.with - r.declined ? "text-rose-300" : "text-zinc-600"}`}>{r.total - r.with - r.declined}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const newGroupId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function GroupBadge({ n }) {
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-violet-500/20 px-1.5 py-0.5 text-[10px] text-violet-200 align-middle"
      title="Consulted together with others: one recording and one score for the group" data-testid="group-badge">
      <Users className="h-3 w-3" />Group{n > 1 ? ` of ${n}` : ""}
    </span>
  );
}

function LeadCard({ l, groupN = 0, recs, onEdit, onDelete, onBilled }) {
  const isNotSold = l.lead_status === "not_sold";
  const hasFollowup = !!(l.followup_date || (l.comments || "").trim() || (l.objection || "").trim());
  const needsFollowup = isNotSold && !hasFollowup;
  const fmtFu = l.followup_date ? new Date(l.followup_date).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;
  const sold = l.lead_status === "sold" || l.lead_status === "sold_pending_payment";
  return (
    <div className={`card-surface p-3.5 ${needsFollowup ? "border-rose-500/30" : ""}`} data-testid={`lead-card-${l.id}`}>
      <button type="button" onClick={onEdit} className="w-full text-left" aria-label={`Open ${l.name}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium truncate">{l.name}{l.group_id && <GroupBadge n={groupN} />}</div>
            <div className="text-xs text-zinc-500 truncate">{l.phone || l.email || "—"}</div>
          </div>
          <StatusPill value={pillStatus(l)} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400">
          <span>{new Date(l.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
          {l.sales_ex && <span>· {l.sales_ex}</span>}
          {l.programme && <span>· {l.programme}</span>}
          {leadMoney(l) && <span className="font-mono text-zinc-200">· <PaidAmount l={l} inline /></span>}
        </div>
        {needsFollowup ? (
          <div className="mt-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border border-rose-500/40 bg-rose-500/10 text-rose-300">No follow-up info</div>
        ) : (l.objection || fmtFu || l.comments) && (
          <div className="mt-2 text-xs text-zinc-500 space-y-0.5">
            {l.objection && <div className="truncate">{l.objection}</div>}
            {fmtFu && <div className="text-amber-400">Follow-up · {fmtFu}</div>}
            {l.comments && <div className="truncate">“{l.comments}”</div>}
          </div>
        )}
      </button>
      <div className="mt-3 pt-2.5 border-t border-[#1f1f1f] flex items-center gap-2">
        <button type="button" onClick={onEdit}
          className={`inline-flex items-center gap-1.5 text-xs py-1.5 ${recs ? "text-blue-400" : "text-amber-300/90"}`}>
          <Mic2 className="h-3.5 w-3.5" />{recs ? `${recs} recording${recs > 1 ? "s" : ""}` : "No recording · add"}
        </button>
        <div className="ml-auto flex items-center gap-1">
          {sold && <BillButton kind="lead" id={l.id} billed={l.billing_done} onBilled={onBilled} />}
          <button type="button" onClick={onEdit} className="p-2 text-zinc-400 hover:text-blue-400" aria-label={`Edit ${l.name}`}><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={onDelete} className="p-2 text-zinc-500 hover:text-rose-400" aria-label={`Delete ${l.name}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint }) {
  const tints = {
    blue: "text-blue-400",
    emerald: "text-emerald-400",
    violet: "text-violet-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };
  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <Icon className={`h-4 w-4 ${tints[tint]}`} strokeWidth={2} />
      </div>
      <div className="font-display text-3xl font-extrabold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

function StatusPill({ value }) {  const cfg = {
    sold: { cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20", label: "Sold" },
    sold_pending_payment: { cls: "text-amber-300 bg-amber-500/10 border-amber-500/20", label: "Sold · pending" },
    not_sold: { cls: "text-zinc-300 bg-zinc-500/10 border-zinc-500/20", label: "Not sold" },
    closed_lost: { cls: "text-rose-300 bg-rose-500/10 border-rose-500/20", label: "Closed · lost" },
    trial_week_paid: { cls: "text-sky-300 bg-sky-500/10 border-sky-500/20", label: "Trial Week Paid" },
  }[value] || { cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20", label: value };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border ${cfg.cls}`}>{cfg.label}</span>
  );
}

function LeadDialog({ mode, initial, current, onDone, leads = [], recCount = {}, pendingRec = null, memberDeclined = false, walkin = null, group = null }) {
  const navigate = useNavigate();
  // Saving a sold consult (or changing a sold member's details): the member checks their details first.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmedRef = useRef("");
  const { activeFacility, effectiveRole, user: me } = useAuth();
  const isHead = atLeast(effectiveRole || me?.role, "manager");   // gym manager or above (CR-0025)
  // Facility taxation config drives GST/VAT label + math. tax_enabled=false (e.g. UAE) means
  // the tax field is fully greyed out and every calc treats tax as 0.
  const taxRate = Number(activeFacility?.tax_rate ?? 0.05);
  const taxEnabled = activeFacility?.tax_enabled !== false;
  const taxName = activeFacility?.tax_name || "GST";
  const taxPctLabel = taxEnabled ? `${taxName} (${(taxRate * 100).toFixed(taxRate * 100 % 1 === 0 ? 0 : 2)}% included in Amount paid)` : `${taxName} · disabled for this gym`;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [f, setF] = useState(() => ({
    name: "", sales_ex: current?.name || "", lead_ex: "", phone: "", email: "",
    three_d_scan: false, lead_status: "not_sold", fe_pif: "",
    programme_started: false, amount_paid: 0, amount_sold: 0, gst: 0, refund_amount: 0,
    mode_of_payment: "", blood_test: false, effective_net_revenue: 0,
    tshirt_given: false, handwritten_card: false, billing_done: false, tshirt_billing_done: false,
    acc_coach: "", followup_date: "", objection: "", objection_other: "", comments: "",
    wa_welcome_tee_card: false, billing_within_24h: false,
    tshirt_billing_done_compliance: false, trainerise_welcome_video: false,
    nutrition_next_day: false, ar_sheet_before_nutrition: false, mid_final_week_booked: false,
    // Membership window & referral (Feb 2026) — leave programme blank so the New Consult
    // form doesn't inherit the previous entry's programme; user picks explicitly each time.
    programme: "", start_date: "", end_date: "",
    referred_by_customer_id: "", referred_by_name: "",
    // Partial-payment reminder (Feb 2026)
    next_payment_due_date: "", next_payment_due_amount: 0, payment_reminder_note: "",
    ...(initial || {}),
    ...(walkin && mode === "create" ? { name: walkin.name, phone: walkin.phone, email: walkin.email } : {}),
    // Consult date — placed AFTER `...initial` so we normalise the raw ISO datetime that
    // Mongo returns (e.g. "2026-02-26T14:30Z") down to yyyy-mm-dd before it reaches the
    // DatePicker. For a fresh New Consult (no initial), defaults to today's date.
    date: initial?.date ? String(initial.date).slice(0, 10) : todayLocal(activeFacility?.timezone),
  }));
  const [busy, setBusy] = useState(false);
  const [another, setAnother] = useState(false);    // someone else was in this consult too (group consult)
  const groupLeft = (group?.queue || []).length;
  const groupmates = mode !== "create" && initial?.group_id ? leads.filter((l) => l.group_id === initial.group_id && l.id !== initial.id) : [];
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState("");
  const [recFile, setRecFile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [objections, setObjectionsList] = useState([]);
  const [priceOverride, setPriceOverride] = useState(false);
  useEffect(() => {
    if (mode === "create") {
      listScripts().then(setScripts).catch(() => {});
      listCustomers().then(setCustomers).catch(() => {});
    }
    getProgrammeCatalog().then((r) => setCatalog(r.items || [])).catch(() => {});
    getObjections().then((r) => setObjectionsList((r.items || []).filter((x) => x.active))).catch(() => setObjectionsList([]));
  }, [mode]);
  // When the active facility has tax disabled (e.g. UAE), force gst → 0 and recompute
  // Effective Net Revenue so the number stays truthful even for edited legacy leads.
  useEffect(() => {
    if (taxEnabled) return;
    setF((s) => {
      if ((Number(s.gst) || 0) === 0) return s;
      const paid = Number(s.amount_paid) || 0;
      const refund = Number(s.refund_amount) || 0;
      return {
        ...s,
        gst: 0,
        effective_net_revenue: Math.max(0, Math.round((paid - refund) * 100) / 100),
      };
    });
  }, [taxEnabled]);
  // Catalog prices are ex-GST; members pay price + GST, and staff enter what was collected.
  // Annual doesn't include blood tests: adding them costs the member ANNUAL_BLOOD_TEST_ADDON more, before
  // GST (Anish, 3 Nov 2026). The 6 Week Challenge price already includes its two.
  const priceOf = (prog, bloodTest = false) => {
    if (!prog) return null;
    const row = (catalog || []).find((x) => x.name && x.name.toLowerCase() === String(prog).toLowerCase());
    if (!row) return null;
    const addon = bloodTest && /\bannual\b/i.test(row.name) && activeFacility?.offers_blood_tests !== false ? ANNUAL_BLOOD_TEST_ADDON : 0;
    const base = Number(row.price || 0) + addon;
    return taxEnabled ? Math.round(base * (1 + taxRate) * 100) / 100 : base;
  };
  // Programme dropdown: every active catalog programme. New Consult and All Sales no longer split
  // the catalog (Anish, 29 Oct 2026: a new member can buy 6 weeks, 12, 24 or Annual at the consult).
  // Falls back to the hardcoded FE_PROGRAMMES list when the catalog hasn't loaded yet
  // so the dropdown is never empty on initial render.
  const fePrograms = (() => {
    const list = (catalog || [])
      .filter((x) => x.active !== false)
      .map((x) => x.name)
      .filter(Boolean);
    return list.length ? list : FE_PROGRAMMES;
  })();
  const set = (k, v) => {
    setF((s) => {
      const next = { ...s, [k]: v };
      // Programme changed → auto-populate amount_sold from catalog (if not manually overridden)
      if (k === "programme" || k === "blood_test") {
        const p = priceOf(next.programme, next.blood_test);
        if (p !== null && p > 0 && !priceOverride) {
          next.amount_sold = p;
        }
      }
      // Track manual override on amount_sold or amount_paid
      if (k === "amount_sold") {
        const catalogPrice = priceOf(next.programme, next.blood_test);
        if (catalogPrice !== null && catalogPrice > 0 && Number(v) !== catalogPrice) {
          setPriceOverride(true);
        }
      }
      // Auto-populate consumption tax using the facility's config (0 when tax_enabled=false)
      if (k === "amount_paid") next.gst = taxEnabled ? taxIncluded(v, taxRate) : 0;
      // Auto-populate Effective Net Revenue = Amount Paid − Refund − Tax
      if (["amount_paid", "refund_amount", "gst"].includes(k)) {
        const paid = Number(next.amount_paid) || 0;
        const refund = Number(next.refund_amount) || 0;
        const gst = Number(next.gst) || 0;
        next.effective_net_revenue = Math.max(0, Math.round((paid - refund - gst) * 100) / 100);
      }
      // Auto-populate end_date from programme + start_date (unless user manually set end_date)
      if (k === "programme" || k === "start_date") {
        const derived = autoEndDate(k === "programme" ? v : next.programme,
                                     k === "start_date" ? v : next.start_date);
        if (derived) next.end_date = derived;
      }
      return next;
    });
  };

  const isSold = f.lead_status === "sold" || f.lead_status === "sold_pending_payment";
  // Nobody starts before paying in full: starting with money owed needs a reason (the server checks too).
  // A new consult saved as sold can take the payment made right there (it becomes the sale's first
  // payment, billed as usual); every later payment is taken on the sale in All Sales.
  const payModes = usePaymentModes(f.mode_of_payment);
  const owed = Math.max(0, Number(f.amount_sold || 0) - Number(f.refund_amount || 0) - Number(f.amount_paid || 0));
  // 6 Week Challenge: paid in full first, no exceptions. Annual: up to 3 monthly payments, they start
  // after the first. Anything else: in full unless a reason is given.
  const prog = (f.programme || "").toLowerCase();
  const plan = prog.includes("annual") ? "annual" : /6[ -]?week|six week|6wc/.test(prog) ? "full" : "other";
  const annualOnPlan = plan === "annual" && Number(f.amount_paid || 0) + 1 >= (Number(f.amount_sold || 0) - Number(f.refund_amount || 0)) / 3;
  // The 6 Week Challenge (and Annual with its free challenge) starts when the nutrition consult is marked done
  // on Accountability (Anish, 23 Oct 2026). Mirrors server _nutrition_gated. Other programmes start on their
  // start date (saving a start date that has arrived starts it).
  const nutriGated = !!initial?.nutrition_consult_done_on
    || (initial?.nutrition_gated ?? (plan === "full" || plan === "annual"));
  const todayIso = new Date().toISOString().slice(0, 10);
  const startsNow = !!f.start_date && f.start_date.slice(0, 10) <= todayIso;
  const startingUnpaid = !nutriGated && isSold && startsNow && !initial?.programme_started && owed > 0 && !annualOnPlan;
  const awaitingNutri = nutriGated && isSold && owed > 0 && !annualOnPlan && !initial?.nutrition_consult_done_on;
  const trainingUnpaid = isSold && !!f.programme_started && !!initial?.programme_started && owed > 0;
  // 6 Week Challenge: the gym's manager may approve an early start, with a reason (CR-0025; any amount paid since 3 Nov).
  const dueTotal = Math.max(0, Number(f.amount_sold || 0) - Number(f.refund_amount || 0));
  const paidPct = dueTotal ? Math.floor((Number(f.amount_paid || 0) / dueTotal) * 100) : 0;
  const headCanApprove = plan === "full" && isHead;

  // Saving without a call recording asks first (and flags the consult for QC).
  const [noRecAsk, setNoRecAsk] = useState(false);
  const [liveRecCount, setLiveRecCount] = useState(null);   // from the Call recordings panel, if open
  const hasRecording = mode === "create" ? !!(recFile || pendingRec) : (liveRecCount ?? (recCount[initial?.id] || 0)) > 0;
  // Edits only ask about this month's consults that haven't been flagged yet: older consults
  // predate recordings, and re-flagging would overwrite who skipped it and when.
  const askAboutRecording = !hasRecording && (mode === "create"
    || (!initial?.recording_skipped_at && String(f.date || "").slice(0, 7) === todayLocal(activeFacility?.timezone).slice(0, 7)));
  const recStats = useMemo(() => {
    const rep = (f.sales_ex || current?.name || "").trim();
    const month = String(f.date || todayLocal()).slice(0, 7);
    const mine = leads.filter((l) => (l.sales_ex || "").trim() === rep && String(l.date || "").slice(0, 7) === month && l.id !== initial?.id);
    const missing = mine.filter((l) => !recCount[l.id]).length;
    return { rep: rep || "You", month: monthLabelOf(`${month}-01`), consults: mine.length + 1, missing: missing + 1 };
  }, [f.sales_ex, f.date, leads, recCount, current?.name, initial?.id]);

  const submit = async (e, { skipRecordingCheck = false, skipReason = "not_uploaded" } = {}) => {
    e?.preventDefault?.();
    if (nameProblem(f.name)) return toast.error(`Name: ${nameProblem(f.name)}`);
    if (!f.phone.trim()) return toast.error("Phone number is required");
    if (isSold && !(f.email || "").trim()) return toast.error("Add the member's email before saving this consult as sold. It's needed to send the bill.");
    if (isSold && !initial?.sold_at && !(initial?.lead_status || "").startsWith("sold")) {
      if (!(f.programme || "").trim()) return toast.error("Pick the programme before marking this consult sold.");
      if (Number(f.amount_paid) > 0 && !(f.mode_of_payment || "").trim()) return toast.error("Pick the payment mode (how they paid) before marking this consult sold.");
    }
    if (isSold && guaranteeProblem(f.guarantee)) return toast.error(guaranteeProblem(f.guarantee));
    if (startingUnpaid && plan === "full" && !headCanApprove) {
      return toast.error("The 6 Week Challenge is paid in full before starting. Record the payment, set a later start date, or ask the gym's manager to approve.");
    }
    if (startingUnpaid && !(f.started_unpaid_reason || "").trim()) {
      return toast.error("They still owe money. Collect it before they start, or give a reason to start anyway.");
    }
    const detailsKey = [f.name, f.phone, f.email].map((v) => (v || "").trim()).join("|");
    const newlySold = mode === "create" || !(initial?.lead_status || "").startsWith("sold");
    const detailsChanged = detailsKey !== [initial?.name, initial?.phone, initial?.email].map((v) => (v || "").trim()).join("|");
    if (isSold && (newlySold || detailsChanged) && confirmedRef.current !== detailsKey) { setConfirmOpen(true); return undefined; }
    // Declined on tape in Sales mode (and no file picked since): no need to ask again.
    if (memberDeclined && askAboutRecording && !skipRecordingCheck) { skipRecordingCheck = true; skipReason = "declined"; }
    if (askAboutRecording && !skipRecordingCheck) { setNoRecAsk(true); return undefined; }
    const skipFlag = askAboutRecording ? { recording_skipped_at: new Date().toISOString(), recording_skipped_by: current?.name || "",
      recording_skipped_reason: skipReason } : {};
    setNoRecAsk(false);
    setBusy(true);
    try {
      if (mode === "create") {
        const gid = group?.id || (another ? newGroupId() : null);
        const created = await createLead({
          group_id: gid,
          name: f.name, sales_ex: f.sales_ex, lead_ex: f.lead_ex, phone: f.phone, email: f.email,
          // Backend accepts a `date` on LeadCreate and stores it verbatim; we send an ISO
          // date string so back-dated data-entry lands on the correct day for MTD/dashboards.
          date: f.date || null,
          programme: f.programme || null,
          start_date: f.start_date || null,
          orientation_date: f.orientation_date || null,
          referred_by_customer_id: f.referred_by_customer_id || null,
          referred_by_name: f.referred_by_name || null,
        });
        // If any other fields set, follow-up patch
        const patch = {};
        [
          "three_d_scan", "lead_status", "fe_pif",
          "amount_paid", "amount_sold", "gst", "mode_of_payment",
          "blood_test", "effective_net_revenue", "tshirt_given", "handwritten_card",
          "billing_done", "tshirt_billing_done", "acc_coach", "followup_date",
          "objection", "objection_other", "comments",
          "wa_welcome_tee_card", "tshirt_billing_done_compliance",
          "trainerise_welcome_video", "mid_final_week_booked",
          "end_date",
          "next_payment_due_date", "next_payment_due_amount", "payment_reminder_note", "started_unpaid_reason",
        ].forEach((k) => {
          if (f[k] !== "" && f[k] !== false && f[k] !== 0 && f[k] != null) patch[k] = f[k];
        });
        if (isSold && cleanGuarantee(f.guarantee)) patch.guarantee = cleanGuarantee(f.guarantee);
        Object.assign(patch, skipFlag);
        if (Object.keys(patch).length) await updateLead(created.id, patch);
        // Keep the questionnaire's answers with this consult
        if (walkin) {
          try { await linkWalkin(walkin.id, created.id); } catch { toast.error("Consult saved, but linking the pre-sale questionnaire failed."); }
        }
        // Link the Sales-mode recording, if this consult came from one
        if (pendingRec) {
          try {
            await attachRecording(pendingRec.id, created.id);
          } catch (err) {
            toast.error("Consult saved, but linking the recording failed. It's listed under 'Recordings not linked to a consult yet'.");
          }
        }
        // Attach the call recording, if one was picked
        if (recFile) {
          try {
            await uploadRecording(null, selectedScript || null, recFile, null, created.id);
            toast.success("Recording attached");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Consult saved, but the recording didn't upload. Open the consult to try again.");
          }
        }
        toast.success("Lead added");
        if (gid && (groupLeft > 0 || another)) {         // more people in this group consult: their form next
          if (isSold && owed > 0) toast(`Sold: take the rest of ${f.name}'s payment on their sale in All Sales`);
          onDone({ date: f.date }, { group: group || { id: gid, queue: [], total: null, n: 1 } });
          return;
        }
        if (isSold && owed > 0) { toast("Sold: take the rest of the payment on its sale in All Sales"); navigate(`/sales-log?lead=${created.id}`); return; }
      } else {
        const { guarantee: _g, ...rest } = f;
        await updateLead(initial.id, { ...rest, ...(isSold ? { guarantee: cleanGuarantee(f.guarantee) } : {}), ...skipFlag });
        toast.success("Lead updated");
        if (isSold && !(initial?.lead_status || "").startsWith("sold")) {
          toast("Sold: take the payment on its sale in All Sales"); navigate(`/sales-log?lead=${initial.id}`); return;
        }
      }
      onDone({ date: f.date });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
      <ConfirmDetails open={confirmOpen}
        details={{ name: f.name, phone: f.phone, email: f.email,
          extra: [["Programme", f.programme], ["Price", Number(f.amount_sold) ? `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(f.amount_sold)}` : ""]] }}
        onEdit={() => setConfirmOpen(false)} onUseSuggestion={(v) => set("email", v)}
        onConfirm={() => { confirmedRef.current = [f.name, f.phone, f.email].map((v) => (v || "").trim()).join("|"); setConfirmOpen(false); submit(); }} />
      <Dialog open={noRecAsk} onOpenChange={setNoRecAsk}>
        <DialogContent className="bg-amber-300 border-amber-400 text-zinc-900 max-w-md sm:rounded-2xl" data-testid="no-recording-warning">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl text-zinc-950">Hey, you have not uploaded a recording.</DialogTitle>
            <DialogDescription className="text-zinc-800 text-base">Do you still want to continue?</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-amber-200/80 border border-amber-500/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-700">Consults in {recStats.month}</div>
              <div className="font-display text-3xl font-extrabold tabular-nums">{recStats.consults}</div>
            </div>
            <div className="rounded-lg bg-amber-200/80 border border-amber-500/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-700">Without a recording</div>
              <div className="font-display text-3xl font-extrabold tabular-nums text-rose-700">{recStats.missing}</div>
            </div>
          </div>
          <div className="text-xs text-zinc-700 -mt-1">{recStats.rep}, including this consult.</div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={(e) => submit(e, { skipRecordingCheck: true })} disabled={busy}
              className="border-zinc-900/30 bg-transparent text-zinc-900 hover:bg-amber-200 rounded-full" data-testid="no-recording-continue">
              Continue without recording
            </Button>
            <Button type="button" variant="outline" onClick={(e) => submit(e, { skipRecordingCheck: true, skipReason: "declined" })} disabled={busy}
              className="border-zinc-900/30 bg-transparent text-zinc-900 hover:bg-amber-200 rounded-full" data-testid="no-recording-declined">
              Member declined recording
            </Button>
            <Button type="button" onClick={() => { setNoRecAsk(false); setTimeout(() => document.getElementById(mode === "create" ? "lead-recording" : "consult-recording-input-anchor")?.scrollIntoView({ block: "center" }), 50); }}
              className="bg-zinc-900 text-amber-300 hover:bg-zinc-800 rounded-full" data-testid="no-recording-upload">
              Upload the recording
            </Button>
          </div>
          <p className="text-[11px] leading-snug text-zinc-700 border-t border-zinc-900/15 pt-2">
            Any consult saved without its call recording will be flagged in the audit to the quality check team.
            If the member said no to being recorded, tap "Member declined recording". Never record without their OK.
          </p>
        </DialogContent>
      </Dialog>
      <DialogHeader>
        <div className="flex items-center justify-between gap-2 pr-8">
          <DialogTitle className="font-display text-xl">{mode === "create" ? "New lead" : `Edit — ${initial.name}`}</DialogTitle>
          {mode === "edit" && (
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-500/60 rounded-full px-2.5 py-1 hover:bg-amber-500/10 transition-colors"
              data-testid="lead-history-btn"
              title="See every edit to this lead"
            >
              <History className="h-3 w-3" /> History
            </button>
          )}
        </div>
        <DialogDescription className="text-zinc-500">
          Log the consult and the sale outcome. Post-sale compliance flags help the coach follow through.
        </DialogDescription>
      </DialogHeader>
      {mode === "edit" && (
        <AuditHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="lead"
          entityId={initial.id}
          entityLabel={initial.name}
        />
      )}
      <form data-testid="sales-form-509" onSubmit={submit} className="space-y-5 mt-2">
        <Section title="Contact">
          <Row>
            <Field label="Consult date" required>
              <DatePicker value={f.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="lead-consult-date-input" />
            </Field>
            <Field label="Full name" required>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} onBlur={() => f.name && set("name", autoFixName(f.name))}
                placeholder="First and last name" aria-invalid={!!(f.name && nameProblem(f.name)) || undefined}
                className={`bg-[#0a0a0a] border-[#262626] ${f.name && nameProblem(f.name) ? "border-rose-500" : ""}`} data-testid="lead-name-input" />
              {f.name && nameProblem(f.name) && <p className="text-[11px] text-rose-400 mt-1">{nameProblem(f.name)}</p>}
            </Field>
            <Field label="Phone" required><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-phone-input" placeholder="Required" /></Field>
          </Row>
          <Row>
            <Field label="Email" required={isSold}><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-email-input" placeholder={isSold ? "Required: the bill is emailed" : "Optional until sold"} />
              <EmailTypoHint email={f.email} name={f.name} onFix={(v) => set("email", v)} testid="lead-email-warning" /></Field>
            <Field label="Sales Ex (rep)">
              <RosterSelect roleType="sales_rep" value={f.sales_ex} onChange={(v) => set("sales_ex", v)} testid="lead-sales-ex-input" />
            </Field>
            <Field label="Lead Nurture Executive">
              <RosterSelect roleType="lead_nurture_exec" value={f.lead_ex} onChange={(v) => set("lead_ex", v)} testid="lead-lead-ex-input" />
            </Field>
          </Row>
          <Row>
            <Field label="Referred by (existing member)">
              <SearchableSelect data-testid="sales-searchable-select-530"
                value={f.referred_by_customer_id || ""}
                onChange={(v) => {
                  if (!v) { set("referred_by_customer_id", ""); set("referred_by_name", ""); return; }
                  const c = customers.find((x) => x.key === v);
                  set("referred_by_customer_id", v);
                  set("referred_by_name", c?.name || "");
                }}
                options={customers.map((c) => ({
                  value: c.key,
                  label: c.name,
                  hint: c.phone || "",
                }))}
                placeholder="None"
                emptyLabel="— none —"
                testid="lead-referral-select"
              />
            </Field>
            <Field label="Programme">
              <Select data-testid="sales-select-549" value={f.programme || ""} onValueChange={(v) => set("programme", v)}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-programme-select"><SelectValue placeholder="Select programme" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  {(fePrograms || []).map((p) => (
                    <SelectItem data-testid="sales-select-item-553" key={p} value={p} className="focus:bg-[#1a1a1a]">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Nutrition orientation">
              <DatePicker value={f.orientation_date || ""} onChange={(e) => set("orientation_date", e.target.value)} data-testid="lead-orientation-date-input" />
            </Field>
            <Field label="Programme start date">
              <DatePicker value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="lead-start-date-input" />
              <div className="mt-1 text-[11px] text-zinc-500">The programme starts on this date (FPA and the app follow it). Client delaying? Move the date.</div>
            </Field>
            <Field label="End date (auto)">
              <DatePicker value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="lead-end-date-input" />
            </Field>
          </Row>
        </Section>

        <Section title="Outcome">
          <Row>
            <Field label="Lead status">
              <Select data-testid="sales-select-570" value={f.lead_status} onValueChange={(v) => set("lead_status", v)}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  {STATUS_OPTIONS.map((o) => (<SelectItem data-testid="sales-select-item-573" key={o.value} value={o.value} className="focus:bg-[#1a1a1a]">{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="FE / PIF / PT">
              <Select data-testid="sales-select-578" value={f.fe_pif || ""} onValueChange={(v) => set("fe_pif", v)}>
                <SelectTrigger data-testid="sales-select-trigger-579" className="bg-[#0a0a0a] border-[#262626]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  {FEPIF.map((v) => (<SelectItem data-testid="sales-select-item-581" key={v} value={v} className="focus:bg-[#1a1a1a]">{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            {nutriGated ? (
              <div className="rounded-lg border border-[#1f1f1f] px-3 py-2.5 bg-[#0d0d0d]" data-testid="programme-start-readonly">
                <div className="text-xs text-zinc-300">Programme started</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {initial?.nutrition_consult_done_on
                    ? `Started ${initial.nutrition_consult_done_on} (nutrition consult done)`
                    : "Starts when the nutrition consult is marked done on Accountability"}
                </div>
              </div>
            ) : null}
          </Row>
          {awaitingNutri && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="start-unpaid-nutri">
              <div className="text-xs text-amber-200">
                Still owes {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{owed.toLocaleString()} ({paidPct}% paid).{" "}
                {plan === "full" ? "The 6 Week Challenge is paid in full before it starts, unless the facility head approves an early start."
                  : "Collect the balance before the programme starts."}{" "}
                If the nutrition consult is marked done before then, the programme still starts, and it&apos;s flagged and emailed to the facility head, Stephanie and Anish.
              </div>
              {initial?.early_start_approved ? (
                <div className="text-xs text-emerald-300" data-testid="early-start-approved">
                  Early start approved by {initial.early_start_approved.by}: &ldquo;{initial.early_start_approved.reason}&rdquo;
                </div>
              ) : isHead ? (
                <Input value={f.started_unpaid_reason || ""} onChange={(e) => set("started_unpaid_reason", e.target.value)}
                  placeholder="As facility head, approve an early start: reason (e.g. balance by Friday)"
                  className="bg-[#0a0a0a] border-amber-500/40" data-testid="early-start-reason" />
              ) : null}
            </div>
          )}
          {startingUnpaid && (
            <div className="rounded-lg border border-rose-500/50 bg-rose-500/10 p-3 space-y-2" data-testid="start-unpaid-warning">
              <div className="text-sm font-semibold text-rose-200">
                Still owes {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{owed.toLocaleString()}.{" "}
                {plan === "full" ? "The 6 Week Challenge is paid in full before starting."
                  : plan === "annual" ? "Annual members start after the first of their 3 monthly payments."
                  : "Nobody starts before paying in full."}
              </div>
              {plan === "full" && !headCanApprove ? (
                <div className="text-xs text-rose-200/80">
                  Record the payment first, or set a later start date.{" "}
                  Only the gym&apos;s manager can approve an early start.
                </div>
              ) : plan === "full" ? (
                <>
                  <div className="text-xs text-rose-200/80">{paidPct}% paid. As the gym&apos;s manager you can approve starting now. It&apos;s recorded as started without full payment. Why?</div>
                  <Input value={f.started_unpaid_reason || ""} onChange={(e) => set("started_unpaid_reason", e.target.value)}
                    placeholder="Reason for approving (e.g. balance by Friday)" className="bg-[#0a0a0a] border-rose-500/40" data-testid="start-unpaid-reason" />
                </>
              ) : (
                <>
                  <div className="text-xs text-rose-200/80">Record the payment first, or set a later start date. To start anyway, say why (e.g. facility head approved, paying on Friday):</div>
                  <Input value={f.started_unpaid_reason || ""} onChange={(e) => set("started_unpaid_reason", e.target.value)}
                    placeholder="Reason to start anyway" className="bg-[#0a0a0a] border-rose-500/40" data-testid="start-unpaid-reason" />
                </>
              )}
            </div>
          )}
          {isSold && !!f.programme_started && owed > 0 && annualOnPlan && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-200" data-testid="annual-plan-note">
              Annual, paying monthly: {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{owed.toLocaleString()} still to come, up to 3 payments in all, one a month.
              {f.next_payment_due_date ? ` Next due ${f.next_payment_due_date}.` : ""}
            </div>
          )}
          {trainingUnpaid && !annualOnPlan && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-xs text-rose-200" data-testid="training-unpaid-note">
              Training while still owing {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{owed.toLocaleString()}
              {initial?.started_unpaid?.reason ? ` · started anyway: “${initial.started_unpaid.reason}” (${initial.started_unpaid.by})` : ""}
            </div>
          )}
          {isSold && <GuaranteeFields value={f.guarantee} onChange={(v) => set("guarantee", v)} />}
          {isSold && (
            <>
              <Row>
                {mode === "create" ? (
                  <NumField data-testid="lead-amount-paid-now" label="Paid now" value={f.amount_paid} onChange={(v) => set("amount_paid", v)} />
                ) : (
                  <Field label="Paid so far (from payments in All Sales)">
                    <Input
                      type="number"
                      step="0.01"
                      value={f.amount_paid || 0}
                      readOnly
                      className="bg-[#0a0a0a] border-[#262626] text-emerald-300 font-mono"
                      data-testid="lead-amount-paid-readonly"
                    />
                  </Field>
                )}
                <NumField data-testid="sales-num-field-604" label="Amount sold (total price)" value={f.amount_sold} onChange={(v) => set("amount_sold", v)} />
                <Field label={taxPctLabel}>
                  <Input
                    type="number"
                    step="0.01"
                    value={taxEnabled ? f.gst : 0}
                    readOnly
                    disabled={!taxEnabled}
                    className={`bg-[#0a0a0a] border-[#262626] font-mono ${taxEnabled ? "text-zinc-400" : "text-zinc-600 opacity-50 cursor-not-allowed"}`}
                    data-testid="lead-gst-field"
                  />
                </Field>
              </Row>
              {mode === "create" && Number(f.amount_paid) > 0 && (
                <Row>
                  <Field label="How they paid" required>
                    <Select value={f.mode_of_payment || ""} onValueChange={(v) => set("mode_of_payment", v)}>
                      <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-payment-mode"><SelectValue placeholder="Pick the payment mode" /></SelectTrigger>
                      <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                        {payModes.map((v) => (<SelectItem key={v} value={v} className="focus:bg-[#1a1a1a]">{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                </Row>
              )}
              <TaxSplit amount={f.amount_paid} rate={taxRate} enabled={taxEnabled} taxName={taxName} testid="lead-tax-split" />
              {/* Refunds return everything the member paid, GST included (e.g. a 5,000 deposit → 5,000 back) */}
              <div className="text-[11px] text-zinc-500 -mb-1" data-testid="refund-rule">
                Refund = the full amount the member paid, including {taxName}. Don&apos;t take {taxName} off.
                {Number(f.amount_paid) > 0 && (
                  <button type="button" className="ml-2 text-blue-400 hover:underline" data-testid="refund-full-btn"
                    onClick={() => set("refund_amount", Number(f.amount_paid))}>Refund in full ({window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(f.amount_paid)})</button>
                )}
              </div>
              <Row>
                <NumField data-testid="sales-num-field-618" label="Refund amount" value={f.refund_amount || 0} onChange={(v) => set("refund_amount", v)} />
                <Field label="Effective Net Revenue (auto)">
                  <Input
                    type="number"
                    step="0.01"
                    value={f.effective_net_revenue || 0}
                    readOnly
                    className="bg-[#0a0a0a] border-[#262626] text-emerald-300 font-mono"
                    data-testid="lead-effective-net-revenue"
                  />
                </Field>
              </Row>
              <div className="text-xs text-zinc-500">Amount Paid, {taxName} and Effective Net Revenue come from the payments on this consult&apos;s sale in All Sales. <span className="text-zinc-300">ENR = Amount Paid − Refund − {taxEnabled ? taxName : "0"}</span>.</div>
              {f.lead_status === "sold_pending_payment" && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="lead-partial-payment-reminder">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-300">Next payment reminder</div>
                  <Row>
                    <Field label="Next payment due date">
                      <DatePicker
                        value={(f.next_payment_due_date || "").slice(0, 10)}
                        onChange={(e) => set("next_payment_due_date", e.target.value)}
                        data-testid="lead-next-payment-due-date"
                      />
                    </Field>
                    <NumField data-testid="sales-num-field-650"
                      label="Expected amount (auto if left blank)"
                      value={f.next_payment_due_amount || 0}
                      onChange={(v) => set("next_payment_due_amount", v)}
                    />
                    <Field label="Note (optional)">
                      <Input
                        value={f.payment_reminder_note || ""}
                        onChange={(e) => set("payment_reminder_note", e.target.value)}
                        placeholder="e.g. Cheque scheduled for month-end"
                        className="bg-[#0a0a0a] border-[#262626]"
                        data-testid="lead-payment-reminder-note"
                      />
                    </Field>
                  </Row>
                  <div className="text-[11px] text-amber-200/80">
                    Everyone with dashboard access will see this in the Pending Payments reminder. Defaults to 30 days after today if left blank. When the balance is fully collected, the reminder disappears. For the sales incentive, money paid by the end of the start-date month counts in that month; a balance paid later counts in the month it's paid.
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-blue-500/25 bg-blue-500/[0.05] px-3 py-2.5 text-xs text-blue-100 flex flex-wrap items-center gap-2" data-testid="payments-in-all-sales">
                <span>Payments are taken on this consult&apos;s sale in All Sales.{mode === "create" || !(initial?.lead_status || "").startsWith("sold") ? " Saving it as sold takes you there." : ""}</span>
                {mode === "edit" && initial?.id && (initial?.lead_status || "").startsWith("sold") && (
                  <Link to={`/sales-log?lead=${initial.id}`} className="ml-auto rounded-full bg-blue-600 hover:bg-blue-500 px-3 py-1 text-white" data-testid="open-sale-payments">Take a payment</Link>
                )}
              </div>
              {mode === "edit" && initial?.id && (initial?.lead_status || "").startsWith("sold") && <BillHistory kind="lead" id={initial.id} />}
            </>
          )}
        </Section>

        <Section title="Objection & notes">
          {f.lead_status === "not_sold" ? (
            <Row>
              <Field label="Objection">
                <Select data-testid="sales-select-700" value={f.objection || "_none"} onValueChange={(v) => set("objection", v === "_none" ? "" : v)}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="lead-objection-select">
                    <SelectValue placeholder="— pick a reason —" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                    <SelectItem data-testid="sales-select-item-705" value="_none" className="focus:bg-[#1a1a1a]">— none —</SelectItem>
                    {objections.map((o) => (
                      <SelectItem data-testid="sales-select-item-707" key={o.name} value={o.name} className="focus:bg-[#1a1a1a]">{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {f.objection === "Other" && (
                <Field label="Other reason (free text)" full>
                  <Input
                    value={f.objection_other || ""}
                    onChange={(e) => set("objection_other", e.target.value)}
                    placeholder="Describe the objection"
                    className="bg-[#0a0a0a] border-[#262626]"
                    data-testid="lead-objection-other-input"
                  />
                </Field>
              )}
              <Field label="Followup date">
                <DatePicker value={f.followup_date || ""} onChange={(e) => set("followup_date", e.target.value)} data-testid="lead-followup-date-input" />
              </Field>
            </Row>
          ) : (
            <div className="text-[11px] text-zinc-500 rounded-lg bg-[#0d0d0d] border border-[#1f1f1f] px-3 py-2 mb-2" data-testid="lead-objection-hidden-hint">
              Objection & follow-up are only tracked when the lead is <span className="text-zinc-300">Not sold</span>. Change the status above to log a re-approach plan.
            </div>
          )}
          <Row>
            <Field label="Comments" full>
              <Textarea data-testid="sales-textarea-734" value={f.comments} onChange={(e) => set("comments", e.target.value)} rows={2} className="bg-[#0a0a0a] border-[#262626]" />
            </Field>
          </Row>
          <Row>
            <Field label="Acc Coach">
              <RosterSelect roleType="accountability_coach" value={f.acc_coach} onChange={(v) => set("acc_coach", v)} testid="lead-acc-coach-input" />
              {isSold && !(f.acc_coach || "").trim() && (
                <div className="mt-1 text-[11px] text-amber-300" data-testid="coach-due-note">
                  Add the accountability coach within 24 hours of the sale{initial?.sold_at ? ` (by ${new Date(new Date(initial.sold_at).getTime() + 86400000).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })})` : ""}. The coach incentive is paid to them.
                </div>
              )}
            </Field>
            <ToggleField data-testid="sales-toggle-field-741" label="Blood test" value={f.blood_test} onChange={(v) => set("blood_test", v)} />
          </Row>
        </Section>

        {mode === "create" && (
          <Section title="Group consult">
            {group && (
              <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-4 py-3 text-sm text-violet-100 mb-2" data-testid="group-consult-note">
                Group consult · person {group.n}{group.total ? ` of ${group.total}` : ""}. Everyone shares the recording and its score;
                each person gets their own consult, outcome and bill.
              </div>
            )}
            {groupLeft === 0 && (
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-violet-500" checked={another} onChange={(e) => setAnother(e.target.checked)}
                  data-testid="group-another" />
                Someone else was in this consult too. Add them next, with the same recording.
              </label>
            )}
            {groupLeft > 0 && <p className="text-xs text-zinc-400">Next: {group.queue[0].name}</p>}
          </Section>
        )}
        {mode === "create" ? (
          <Section title="Call recording">
            {pendingRec ? (
              <div className="flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200" data-testid="pending-recording">
                <Mic2 className="h-4 w-4 shrink-0" />
                Sales-mode recording attached{pendingRec.seconds ? ` · ${Math.max(1, Math.round(pendingRec.seconds / 60))} min` : ""}. It&apos;s saved in the cloud.
              </div>
            ) : memberDeclined ? (
              <div className="flex items-center gap-3 rounded-lg border border-[#333] bg-[#0a0a0a] px-4 py-3 text-sm text-zinc-300" data-testid="declined-recording">
                <Mic2 className="h-4 w-4 shrink-0 text-zinc-500" />
                Member declined recording, so nothing was recorded or kept. This consult is saved as &quot;Member declined&quot;.
              </div>
            ) : (
            <label htmlFor="lead-recording" className="flex items-center gap-3 rounded-lg border border-dashed border-[#333] px-4 py-3 cursor-pointer hover:border-blue-500/50 bg-[#0a0a0a]">
              <Mic2 className="h-4 w-4 text-zinc-500 shrink-0" />
              <div className="text-sm text-zinc-300 truncate flex-1">{recFile ? recFile.name : "Attach the consult call recording (audio file)"}</div>
              <input id="lead-recording" type="file" accept="audio/*,.mp3,.wav,.m4a,.webm,.mp4,.mpeg,.ogg,.flac" className="hidden" onChange={(e) => setRecFile(e.target.files?.[0] || null)} data-testid="lead-recording-input" />
            </label>
            )}
          </Section>
        ) : (
          <>
            {initial?.ghl_sync && <GhlSyncLine lead={initial} />}
            {initial?.group_id && (
              <p className="text-sm text-violet-200" data-testid="group-mates">
                <Users className="h-4 w-4 inline mr-1" />Group consult{groupmates.length ? ` with ${groupmates.map((l) => l.name).join(", ")}` : ""}. The recording and its score are shared.
              </p>
            )}
            {initial?.walkin_form_id && <LeadWalkin leadId={initial.id} />}
            <Section title="Call recordings">
              <div id="consult-recording-input-anchor" />
              <ConsultRecordings leadId={initial.id} onCount={setLiveRecCount} />
            </Section>
          </>
        )}

        {isSold && (
          <Section title="Post-sale compliance">
            <div className="rounded-lg border border-[#262626] bg-[#0a0a0a] px-3 py-2.5 text-xs text-zinc-400" data-testid="post-sale-compliance-moved-note">
              Post-sale compliance now lives on the <span className="text-zinc-200 font-medium">Accountability</span> page.
              Open the client&apos;s row there — the assigned coach or member manager can tick these boxes as they complete each step.
            </div>
          </Section>
        )}

        {/* Phones: the save button stays on screen while scrolling the long form */}
        <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:bg-[#0f0f0f] max-sm:border-t max-sm:border-[#1f1f1f]">
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full max-sm:w-full max-sm:h-11" data-testid="lead-submit-btn">
            {busy ? "Saving…" : mode === "create" ? "Create lead" : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="eyebrow mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
}
function Field({ label, children, required, full }) {
  return (
    <div className={full ? "md:col-span-3" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}{required && <span className="text-rose-400 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
// Amounts on the consult list (CR-0004/0005): only sold consults have money. Show what's been
// collected; while a balance is outstanding, also the price and what's still due.
function leadMoney(l) {
  if (l.lead_status !== "sold" && l.lead_status !== "sold_pending_payment") return null;
  const paid = Number(l.amount_paid) || 0;
  const sold = Number(l.amount_sold) || 0;
  const due = Math.round((sold - (Number(l.refund_amount) || 0) - paid) * 100) / 100;
  if (!paid && !sold) return null;
  return { paid, sold, due };
}
function PaidAmount({ l, inline = false }) {
  const m = leadMoney(l);
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  if (!m) return inline ? null : <span className="text-zinc-600">—</span>;
  const due = m.due > 0 && (
    <span className={`text-[11px] text-amber-300 ${inline ? "ml-1" : "block"}`} data-testid={`lead-due-${l.id}`}>
      {cur}{fmtINR(m.due)} due{inline ? "" : <span className="text-zinc-500"> of {cur}{fmtINR(m.sold)}</span>}
    </span>
  );
  return <>{cur}{fmtINR(m.paid)}{due}</>;
}
// Keeps the typed text so a leading 0 can't stick ("023100", CR-0006): for type="number"
// inputs React leaves the box alone when the text parses to the same number it already has.
// A zero amount shows as an empty box with a 0 placeholder, so typing never starts after a 0.
function NumField({ label, value, onChange }) {
  const show = (v) => (Number(v) ? String(v) : "");
  const [text, setText] = useState(() => show(value));
  useEffect(() => {
    setText((t) => ((parseFloat(t) || 0) === (Number(value) || 0) ? t : show(value)));
  }, [value]);
  return (
    <Field label={label}>
      <Input data-testid="sales-input-808" type="number" step="0.01" inputMode="decimal" placeholder="0" value={text}
        onChange={(e) => {
          const t = e.target.value.replace(/^0+(?=\d)/, "");
          setText(t);
          onChange(parseFloat(t) || 0);
        }}
        className="bg-[#0a0a0a] border-[#262626]" />
    </Field>
  );
}
function ToggleField({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-[#1f1f1f] px-3 py-2.5 bg-[#0d0d0d]">
      <div className="text-xs text-zinc-300">{label}</div>
      <Switch data-testid="sales-switch-816" checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

function SlabCard({ title, rows }) {
  const fmt = (n) => n == null ? "∞" : (n >= 100000 ? `${(n/100000).toFixed(n%100000===0?0:1)}L` : `${n/1000}K`);
  return (
    <div className="card-surface p-5">
      <div className="eyebrow">{title}</div>
      <table className="w-full text-sm mt-3">
        <tbody>
          {(rows || []).map((r, i) => (
            // Slabs are ordered ranges — `min-max-rate` is a stable natural key that survives
            // slab edits (index would cause React to reuse cells across rate/min changes).
            <tr key={`${r.min}-${r.max}-${r.rate}-${i}`} className="border-b border-[#1a1a1a] last:border-0">
              <td className="py-2 text-zinc-400 font-mono text-xs">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(r.min)} – {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(r.max)}</td>
              <td className="py-2 text-right font-display font-bold text-emerald-400 tabular-nums">{(r.rate*100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FEMonthlyStrip() {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  useEffect(() => { feMonthly(year, month).then(setD); }, [year, month]);
  const shift = (n) => {
    let y = year, m = month + n;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };
  return (
    <section className="card-surface px-4 py-2.5 flex items-center gap-x-5 gap-y-2 flex-wrap" data-testid="fe-monthly-strip">
      <div className="flex items-center gap-2 shrink-0">
        <div className="eyebrow">Snapshot</div>
        <div className="inline-flex items-center rounded-full border border-[#262626] bg-[#0a0a0a] px-0.5">
          <button onClick={() => shift(-1)} className="px-1.5 py-0.5 rounded-full hover:bg-[#1a1a1a]" data-testid="strip-prev-month" aria-label="Previous month"><span className="text-zinc-400">‹</span></button>
          <div className="text-xs text-zinc-100 min-w-[4.5rem] text-center font-medium">{MONTHS[month - 1]} {year}</div>
          <button onClick={() => shift(1)} className="px-1.5 py-0.5 rounded-full hover:bg-[#1a1a1a]" data-testid="strip-next-month" aria-label="Next month"><span className="text-zinc-400">›</span></button>
        </div>
      </div>
      <div className="flex items-center gap-x-5 gap-y-1 flex-wrap flex-1">
        <MiniStat label="Consults" value={d?.consults ?? "—"} testid="strip-consults" />
        <MiniStat label="Sold" value={d?.sold ?? "—"} tint="emerald" testid="strip-sold" />
        <MiniStat label="Conv." value={d ? `${((d.conversion || 0) * 100).toFixed(0)}%` : "—"} tint="violet" testid="strip-conv" />
        <MiniStat label="Started" value={d?.programmes_started ?? "—"} tint="blue" testid="strip-started" />
        <MiniStat label="Collected" value={d ? `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(d.revenue_collected)}` : "—"} tint="amber" testid="strip-collected" />
        <MiniStat label="Incentive" value={d ? `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(d.incentive_this_month)}` : "—"} tint="rose" testid="strip-incentive" />
      </div>
      <Link data-testid="sales-link-860" to="/sales/dashboard" className="text-xs text-blue-400 hover:text-blue-300 shrink-0">Full dashboard →</Link>
    </section>
  );
}

function MiniStat({ label, value, tint, testid }) {
  const tints = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300", blue: "text-blue-300", violet: "text-violet-300" };
  return (
    <div className="flex items-baseline gap-1.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${tints[tint] || "text-zinc-100"}`}>{value}</span>
    </div>
  );
}

function FollowupsDueStrip({ onRefresh }) {
  const [data, setData] = useState({ count: 0, overdue: [], due_soon: [] });
  const [openId, setOpenId] = useState(null);
  // Collapsed by default so the consults list is visible; the choice is remembered per browser.
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("followups_expanded") === "1"; } catch { return false; }
  });
  const toggle = () => setExpanded((v) => {
    try { localStorage.setItem("followups_expanded", v ? "0" : "1"); } catch { /* ignore */ }
    return !v;
  });
  const load = () => listFollowupDue().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const merged = [...(data.overdue || []).map((r) => ({ ...r, bucket: "overdue" })),
                  ...(data.due_soon || []).map((r) => ({ ...r, bucket: "due_soon" }))];
  if (merged.length === 0) return null;
  const opened = merged.find((r) => r.id === openId);
  return (
    <section data-testid="followups-due-strip">
      <button
        type="button"
        onClick={toggle}
        className="w-full card-surface px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-[#141414] transition-colors"
        aria-expanded={expanded}
        data-testid="followups-toggle"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${expanded ? "" : "-rotate-90"}`} />
          <span className="eyebrow">Not-Sold follow-ups</span>
          <span className="text-xs text-zinc-300 tabular-nums">{merged.length} due</span>
          {data.overdue?.length > 0 && <span className="text-[10px] font-medium text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded-full px-2 py-0.5">{data.overdue.length} overdue</span>}
        </div>
        <span className="text-[11px] text-zinc-500">{expanded ? "Hide" : "Show"}</span>
      </button>
      {expanded && (
      <div className="text-xs text-zinc-500 mt-2 mb-2">Log a follow-up to keep the workflow moving. After 2 failed follow-ups the lead auto-closes.</div>
      )}
      {expanded && (
      <div className="card-surface overflow-hidden">
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-3 py-2 font-medium">Lead</th>
                <th className="px-3 py-2 font-medium">Rep</th>
                <th className="px-3 py-2 font-medium">Objection</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Due</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((r) => (
                <tr key={r.id} className={`border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] ${r.bucket === "overdue" ? "bg-rose-500/[0.04]" : ""}`} data-testid={`followup-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-zinc-100">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">{r.phone || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300 text-xs">{r.sales_ex || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs max-w-[14rem]">
                    <div className="truncate">{r.objection || <span className="text-zinc-700">—</span>}</div>
                    {r.objection === "Other" && r.objection_other && <div className="text-[10px] text-zinc-500 truncate">{r.objection_other}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#262626] bg-[#0d0d0d] text-zinc-300 text-[10px]">
                      Attempt {(r.followup_stage || 0) + 1} of 2
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                    {r.followup_date ? (
                      <span className={r.bucket === "overdue" ? "text-rose-300 font-medium" : "text-amber-300"}>
                        {new Date(r.followup_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                      </span>
                    ) : (
                      <span className="text-zinc-500">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => setOpenId(r.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
                      data-testid={`log-followup-btn-${r.id}`}
                    >
                      <Clock className="h-3 w-3" /> Log follow-up
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
      <Dialog open={!!openId} onOpenChange={(v) => !v && setOpenId(null)}>
        {opened && (
          <FollowupDialog
            lead={opened}
            onDone={() => { setOpenId(null); load(); onRefresh && onRefresh(); }}
          />
        )}
      </Dialog>
    </section>
  );
}

function FollowupDialog({ lead, onDone }) {
  const [comment, setComment] = useState("");
  const [stillNotSold, setStillNotSold] = useState(true);
  const [busy, setBusy] = useState(false);
  const stage = lead.followup_stage || 0;
  const isFinal = stage >= 1; // this attempt will bring stage to 2 → auto-close

  const submit = async () => {
    if (!comment.trim()) return toast.error("Add a comment about the follow-up");
    setBusy(true);
    try {
      const updated = await logLeadFollowup(lead.id, { comment: comment.trim(), still_not_sold: stillNotSold });
      if (updated.followup_closed && stillNotSold) {
        toast.success("Lead auto-closed after 2 unsuccessful follow-ups");
      } else if (!stillNotSold) {
        toast.success("Follow-up logged — mark the sale as Sold in the edit panel");
      } else {
        toast.success("Follow-up logged · next reminder in 30 days");
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log follow-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="followup-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Log follow-up · {lead.name}</DialogTitle>
        <DialogDescription className="text-zinc-500">
          {isFinal
            ? "This is the final follow-up. If still Not Sold, the lead will auto-close."
            : "After this attempt we'll schedule the next reminder 30 days from today."}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <div className="text-xs text-zinc-400 rounded-lg border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
          <div><span className="text-zinc-500">Objection:</span> {lead.objection || "—"} {lead.objection === "Other" && lead.objection_other ? `· ${lead.objection_other}` : ""}</div>
          <div className="mt-1"><span className="text-zinc-500">Attempt:</span> {stage + 1} of 2</div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Follow-up notes *</Label>
          <Textarea
            value={comment} onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            placeholder="Summary of the conversation, agreement, next step…"
            data-testid="followup-comment-input"
          />
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3 py-2">
          <Switch checked={stillNotSold} onCheckedChange={setStillNotSold} data-testid="followup-still-not-sold-toggle" />
          <div className="text-sm">
            <div className="text-zinc-200">Still Not Sold?</div>
            <div className="text-[11px] text-zinc-500">
              {stillNotSold
                ? (isFinal ? "Lead will auto-close after this attempt." : "We'll remind you in 30 days.")
                : "Toggle off if the prospect converted — remember to also edit the lead and mark it Sold."}
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="followup-submit-btn">
          {busy ? "Logging…" : (isFinal && stillNotSold ? "Log & close lead" : "Log follow-up")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function PaymentsLedger({ leadId, initialPayments = [], onChange }) {
  const askDelete = useDeleteAsk();
  const [rows, setRows] = useState(initialPayments);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("");
  const payModes = usePaymentModes(mode);
  const [note, setNote] = useState("");
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  // Paid together: this one payment also pays other members' memberships (a couple, friends). [{lead_id, amount}]
  const [also, setAlso] = useState([]);
  const [candidates, setCandidates] = useState(null);
  const total = rows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const jointTotal = (Number(amount) || 0) + also.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const startAlso = async () => {
    const list = candidates || await jointPaymentCandidates(leadId).catch(() => []);
    setCandidates(list);
    if (!list.length) { toast.error("No other consult here that still owes money. Mark the other member sold first."); return; }
    const next = list.find((c) => !also.some((a) => a.lead_id === c.id)) || list[0];
    setAlso((a) => [...a, { lead_id: next.id, amount: next.owed || "" }]);
  };

  const refresh = async () => {
    const list = await listLeadPayments(leadId);
    setRows(list || []);
    onChange && onChange(list || []);
  };
  useEffect(() => { if (leadId) refresh(); }, [leadId]);

  const add = async (e) => {
    e.preventDefault();
    const a = Number(amount);
    if (!a || a <= 0) return toast.error("Enter amount > 0");
    if (!date) return toast.error("Enter payment date");
    if (String(date).slice(0, 10) > todayLocal()) return toast.error("A payment can't be dated in the future. Record it on the day the money comes in.");
    setBusy(true);
    try {
      const payload = { amount: a, date, mode: mode || payModes[0], note };
      if (fee !== "" && !isNaN(Number(fee))) payload.platform_fee = Number(fee);
      if (also.length) {
        if (also.some((x) => !x.lead_id || !(Number(x.amount) > 0))) { setBusy(false); return toast.error("Pick each member and enter their share"); }
        payload.also_for = also.map((x) => ({ lead_id: x.lead_id, amount: Number(x.amount) }));
      }
      await addLeadPayment(leadId, payload);
      await refresh();
      setAmount(""); setNote(""); setFee(""); setAlso([]);
      toast.success(also.length ? `One payment for ${also.length + 1} members added. One bill goes to this member.` : "Payment added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pid) => {
    const reason = askDelete("this payment", "Delete this payment?");
    if (reason === null) return;
    try {
      if (deletionRequested(await deleteLeadPayment(leadId, pid, reason))) return;
      await refresh();
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="rounded-xl border border-[#1f1f1f] bg-[#0a0a0a] p-4" data-testid="payments-ledger">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Payments received</div>
          <div className="text-sm text-zinc-300">
            Total: <span className="text-emerald-300 font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(total)}</span> · {rows.length} entr{rows.length === 1 ? "y" : "ies"}
          </div>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto mb-3 border border-[#1a1a1a] rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium">Note</th>
                <th className="px-3 py-2 font-medium text-right">Amount</th>
                <th className="px-3 py-2 font-medium text-right" title="Processing fee">Fee</th>
                <th className="px-3 py-2 font-medium text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{new Date(p.date).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-zinc-400">{p.mode || "—"}</td>
                  <td className="px-3 py-2 text-zinc-500 truncate max-w-[12rem]" title={p.note}>
                    {p.joint_id && <span className="mr-1 rounded bg-violet-500/20 px-1 text-violet-200">{p.paid_by_lead_id ? "Paid by another member" : "Paid together"}</span>}
                    {p.note || (p.joint_id ? "" : "—")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(p.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-300">{(p.platform_fee || 0) > 0 ? `-${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(p.platform_fee)}` : <span className="text-zinc-700">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => remove(p.id)} className="text-rose-400 hover:text-rose-300 p-1" data-testid={`delete-payment-${p.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Amount *</div>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 10000" className="bg-[#0f0f0f] border-[#262626] font-mono" data-testid="new-payment-amount" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Date *</div>
          <DatePicker value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-payment-date" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Mode</div>
          <Select data-testid="sales-select-1146" value={mode || payModes[0]} onValueChange={setMode}>
            <SelectTrigger className="bg-[#0f0f0f] border-[#262626]" data-testid="new-payment-mode"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {payModes.map((v) => (<SelectItem data-testid="sales-select-item-1149" key={v} value={v} className="focus:bg-[#1a1a1a]">{v}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1" title="Auto-filled from mode + facility fee catalog. Override if the card charged a different rate.">Fee (override)</div>
          <Input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="auto" className="bg-[#0f0f0f] border-[#262626] font-mono text-rose-300" data-testid="new-payment-fee" />
        </div>
        <div className="md:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Note</div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="bg-[#0f0f0f] border-[#262626]" data-testid="new-payment-note" />
        </div>
        <div className="col-span-2 sm:col-span-3 space-y-2" data-testid="paid-together">
          {also.map((x, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-zinc-400">Also pays for</span>
              <select value={x.lead_id} onChange={(e) => {
                const c = (candidates || []).find((k) => k.id === e.target.value);
                setAlso((a) => a.map((y, j) => (j === i ? { lead_id: e.target.value, amount: c?.owed || y.amount } : y)));
              }} className="bg-[#0f0f0f] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100" data-testid={`paid-together-member-${i}`}>
                {(candidates || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.programme ? ` · ${c.programme}` : ""}{c.group ? " · same consult" : ""}</option>
                ))}
              </select>
              <Input type="number" value={x.amount} onChange={(e) => setAlso((a) => a.map((y, j) => (j === i ? { ...y, amount: e.target.value } : y)))}
                placeholder="Their share" className="w-32 bg-[#0f0f0f] border-[#262626] font-mono" data-testid={`paid-together-amount-${i}`} />
              <button type="button" className="text-xs text-zinc-500 underline" onClick={() => setAlso((a) => a.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <button type="button" className="text-violet-300 underline" onClick={startAlso} data-testid="paid-together-add">
              {also.length ? "Add another member" : "This payment also pays for another member's membership"}
            </button>
            {also.length > 0 && (
              <span className="text-zinc-400">
                One payment of <span className="font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(jointTotal)}</span>.
                Amount above is this member&apos;s share. Each keeps their own membership; one bill goes to this member.
              </span>
            )}
          </div>
        </div>
        <Button type="button" onClick={add} disabled={busy} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full inline-flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-colors disabled:opacity-60" data-testid="add-payment-btn" data-write>
          <Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add payment"}
        </Button>
      </div>
    </div>
  );
}


// 16–22 Oct 2026: tell the team, for a week, that FE payments moved to All Sales.
const PAYMENTS_MOVED_UNTIL = "2026-10-23";
function PaymentsMovedNotice() {
  const key = "fpa_payments_moved_notice_closed";
  const [closed, setClosed] = useState(() => { try { return localStorage.getItem(key) === "1"; } catch { return false; } });
  if (closed || todayLocal() >= PAYMENTS_MOVED_UNTIL) return null;
  const close = () => { setClosed(true); try { localStorage.setItem(key, "1"); } catch { /* private window */ } };
  return (
    <div className="rounded-xl border border-blue-500/30 bg-blue-500/[0.06] px-4 py-3 flex items-start gap-3" role="status" data-testid="payments-moved-notice">
      <div className="text-sm text-blue-100">
        <span className="font-semibold">FE payments are now logged in All Sales.</span>{" "}
        Mark the consult Sold here, then take the first payment, balances and instalments on its sale in{" "}
        <Link to="/sales-log" className="underline">All Sales</Link>. Bills still go out by themselves.
        An existing member redoing the 6 Week Challenge goes straight to All Sales (Existing member 6WC), no consult needed.
      </div>
      <button type="button" onClick={close} className="ml-auto text-blue-200/70 hover:text-blue-100 text-xs shrink-0" aria-label="Dismiss">Got it</button>
    </div>
  );
}


// Result guarantee promised at the consult (CR-0042): coaches see it on Accountability.
function GuaranteeFields({ value, onChange }) {
  const g = value || {};
  const setPart = (key, field, v) => onChange({ ...g, [key]: { dir: "lose", ...(g[key] || {}), [field]: v } });
  const problem = guaranteeProblem(g);
  const part = (key, label, unit) => (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`guarantee-${key}`}>
      <span className="text-xs text-zinc-400 w-14">{label}</span>
      <div className="inline-flex rounded-md border border-[#262626] overflow-hidden">
        {["lose", "gain"].map((d) => (
          <button key={d} type="button" onClick={() => setPart(key, "dir", d)}
            data-testid={`guarantee-${key}-${d}`}
            className={`px-2.5 py-1 text-xs ${(g[key]?.dir || "lose") === d ? "bg-emerald-500/20 text-emerald-200" : "bg-[#0a0a0a] text-zinc-400 hover:text-zinc-200"}`}>
            {d === "lose" ? "Lose" : "Gain"}
          </button>
        ))}
      </div>
      <Input type="number" min="0" step="0.5" inputMode="decimal" placeholder="min" value={g[key]?.min ?? ""}
        onChange={(e) => setPart(key, "min", e.target.value)} data-testid={`guarantee-${key}-min`}
        className="bg-[#0a0a0a] border-[#262626] w-20 h-8" />
      <span className="text-zinc-500 text-xs">to</span>
      <Input type="number" min="0" step="0.5" inputMode="decimal" placeholder="max" value={g[key]?.max ?? ""}
        onChange={(e) => setPart(key, "max", e.target.value)} data-testid={`guarantee-${key}-max`}
        className="bg-[#0a0a0a] border-[#262626] w-20 h-8" />
      <span className="text-zinc-500 text-xs">{unit}</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-[#262626] bg-[#0a0a0a]/50 p-3 space-y-2" data-testid="guarantee-fields">
      <div className="text-xs font-semibold text-zinc-300">Result guarantee <span className="font-normal text-zinc-500">(optional, shown to the coaches on Accountability)</span></div>
      {part("weight", "Weight", "kg")}
      {part("inches", "Inches", "inches")}
      {problem && <div className="text-xs text-rose-300" data-testid="guarantee-problem">{problem}</div>}
    </div>
  );
}
